import {
	Alert,
	ButtonDropdownField,
	Checkbox,
	ComboBox,
	DropdownList,
	FlexColumn,
	FlexRow,
	Label,
	Text,
	TextField,
	IconTooltip,
} from '@forecast-it/design-system';
import React, {useEffect, useMemo, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {CurrencyOption, formatCustomFieldCurrencyLabel, getCustomFieldCurrencyOptions} from '../../../../data/currencyHelpers';
import {HYPERLINK_MAX_LENGTH} from '../../../shared/util/customFieldValueValidation';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {isTicketingAvailable} from '../../../shared/util/ticketingAvailability';
import {CustomFieldType, CustomFieldTypeLabelIds, EntityType, EntityTypeLabels, supportsReadOnly} from '../Constants';
import {AvaRefOption} from './ava-custom-field-dropdown/useAvaRefOptions';
import {isCustomFieldTypeVisible} from './customFieldTypeVisibility';
import {isListVisibilityAvailable, ListVisibility} from './listVisibility';
import {ListVisibilitySection} from './ListVisibilitySection';
import styled from 'styled-components';

type CustomFieldDefinitionFormProps = {
	entityType: EntityType;
	setEntityType?: (value: EntityType) => void;
	displayName: string;
	setDisplayName: (value: string) => void;
	fieldKey: string;
	setFieldKey?: (value: string) => void;
	customFieldType: CustomFieldType;
	setCustomFieldType?: (value: CustomFieldType) => void;
	avaRefOptions: AvaRefOption[] | null;
	selectedAvaRef: AvaRefOption;
	selectAvaRefOption: (refOption: AvaRefOption) => void;
	readOnly: boolean;
	setReadOnly: (value: boolean) => void;
	errorMessage?: string | undefined;
	listVisibility?: ListVisibility;
	setListVisibility?: (value: ListVisibility) => void;
	initialListVisibility?: ListVisibility;
	// A Currency field's currency. Only the create modal passes setCurrencyCode; the edit form shows it disabled.
	currencyCode?: string | null;
	setCurrencyCode?: (value: string) => void;
	// Listed first in the currency picker.
	companyCurrency?: string | null;
};

// The note the create form (not the edit form) shows for a type whose behaviour is not obvious from its
// name. Only the Hyperlink note uses {maxLength}; react-intl ignores values a message does not mention.
const TYPE_INFO_MESSAGE: Partial<Record<CustomFieldType, string>> = {
	[CustomFieldType.BOOLEAN]: 'settings.custom-fields.create_modal.boolean_info',
	[CustomFieldType.HYPERLINK]: 'settings.custom-fields.create_modal.hyperlink_info',
	[CustomFieldType.CURRENCY]: 'settings.custom-fields.create_modal.currency_info',
	[CustomFieldType.MULTI_SELECT]: 'custom_field.multi_select.create_info',
};

const StyledContainer = styled.div`
	width: 600px;
`;

export const CustomFieldDefinitionForm = ({
	entityType,
	setEntityType,
	displayName,
	setDisplayName,
	fieldKey,
	setFieldKey,
	customFieldType,
	setCustomFieldType,
	avaRefOptions,
	selectedAvaRef,
	selectAvaRefOption,
	readOnly,
	setReadOnly,
	errorMessage,
	listVisibility,
	setListVisibility,
	initialListVisibility,
	currencyCode,
	setCurrencyCode,
	companyCurrency,
}: CustomFieldDefinitionFormProps): React.JSX.Element => {
	const {formatMessage} = useIntl();
	// Only the create modal lets the type be picked; the edit form disables it and has no note to show.
	const isCreating = !!setCustomFieldType;
	const dummyRef = useRef<HTMLDivElement>(null);
	const visibleCustomFieldTypeLabels = (Object.entries(CustomFieldTypeLabelIds) as [CustomFieldType, string][])
		.filter(([key]) => isCustomFieldTypeVisible(key))
		.map(([key, id]): [CustomFieldType, string] => [key, formatMessage({id})]);
	const isCurrency = customFieldType === CustomFieldType.CURRENCY;
	// The same catalog rule as every other currency picker. A field's own currency stays listed even if the
	// catalog no longer offers it. The edit form cannot change the currency, so it lists only the field's own.
	const currencyOptions = useMemo((): CurrencyOption[] => {
		if (!isCurrency) return [];
		if (!setCurrencyCode) {
			return currencyCode ? [{value: currencyCode, label: formatCustomFieldCurrencyLabel(currencyCode)}] : [];
		}
		return getCustomFieldCurrencyOptions(
			companyCurrency,
			hasFeatureFlag('sage_intacct_requested_currencies_for_forecast'),
			currencyCode
		);
	}, [isCurrency, setCurrencyCode, companyCurrency, currencyCode]);

	// TICKET only applies when embedded ticketing is available; hide it from the "Applies to" dropdown otherwise.
	const visibleEntityTypeLabels = (Object.entries(EntityTypeLabels) as [EntityType, string][]).filter(
		([key]) => key !== EntityType.TICKET || isTicketingAvailable()
	);

	useEffect(() => {
		//Fix buggy Radix button focus on open modal
		const timeout = setTimeout(() => {
			dummyRef.current?.focus();
		}, 0);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<StyledContainer>
			<FlexColumn gap={'l'}>
				<FlexColumn>
					<FlexRow>
						<div ref={dummyRef} tabIndex={-1} />
						<Label>
							<FormattedMessage id={'settings.custom-fields.applies_to'} />
						</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: 'settings.custom-fields.applies_to'})}
								details={formatMessage({
									id: 'settings.custom-fields.create_modal.tooltip.applies_to.description',
								})}
							/>
						</IconTooltip>
					</FlexRow>
					<DropdownList>
						<DropdownList.Trigger>
							<ButtonDropdownField disabled={!setEntityType}>
								<Text ellipsis={true} size={'1'}>
									{EntityTypeLabels[entityType]}
								</Text>
							</ButtonDropdownField>
						</DropdownList.Trigger>
						{setEntityType && (
							<DropdownList.Content offset={10} useTriggerWidth>
								{visibleEntityTypeLabels.map(([key, label]) => (
									<DropdownList.Item key={key} onClick={() => setEntityType(key)}>
										{label}
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						)}
					</DropdownList>
				</FlexColumn>
				<FlexColumn>
					<FlexRow>
						<Label size={'2'}>
							<FormattedMessage id={'settings.custom-fields.displayName'} />
						</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: 'settings.custom-fields.displayName'})}
								details={formatMessage({
									id: 'settings.custom-fields.create_modal.tooltip.display_name.description',
								})}
							/>
						</IconTooltip>
					</FlexRow>
					<TextField
						value={displayName}
						onValueChange={setDisplayName}
						size="large"
						width="flex"
						data-cy={'custom-field-display-name'}
					/>
				</FlexColumn>
				<FlexColumn>
					<FlexRow>
						<Label>
							<FormattedMessage id={'settings.custom-fields.key'} />
						</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: 'settings.custom-fields.key'})}
								details={formatMessage({id: 'settings.custom-fields.create_modal.tooltip.key.description'})}
							/>
						</IconTooltip>
					</FlexRow>
					<TextField
						disabled={!setFieldKey}
						value={fieldKey}
						onValueChange={setFieldKey}
						size="large"
						width="flex"
						data-cy="custom-field-key"
					/>
					{!!errorMessage && <Text color="error">{errorMessage}</Text>}
				</FlexColumn>
				<FlexColumn>
					<FlexRow>
						<Label>
							<FormattedMessage id={'common.type'} />
						</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: 'common.type'})}
								details={formatMessage({id: 'settings.custom-fields.create_modal.tooltip.type.description'})}
							/>
						</IconTooltip>
					</FlexRow>
					<DropdownList>
						<DropdownList.Trigger>
							<ButtonDropdownField disabled={!setCustomFieldType}>
								<Text ellipsis={true} size={'1'}>
									{formatMessage({id: CustomFieldTypeLabelIds[customFieldType]})}
								</Text>
							</ButtonDropdownField>
						</DropdownList.Trigger>
						{setCustomFieldType && (
							<DropdownList.Content offset={10} useTriggerWidth>
								{visibleCustomFieldTypeLabels.map(([key, label]) => (
									<DropdownList.Item key={key} onClick={() => setCustomFieldType(key)}>
										{label}
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						)}
					</DropdownList>
				</FlexColumn>
				{isCurrency && (
					<ComboBox
						label={formatMessage({id: 'common.currency'})}
						helperText={formatMessage({id: 'settings.custom-fields.create_modal.currency_helper'})}
						defaultItems={currencyOptions}
						value={currencyCode ?? null}
						onChange={(key: string | null) => {
							// Clearing the text reports no key; the field keeps its currency.
							if (key) {
								setCurrencyCode?.(key);
							}
						}}
						// disabled only styles the input; isDisabled also locks the button that opens the list.
						disabled={!setCurrencyCode}
						isDisabled={!setCurrencyCode}
						width={ComboBox.WIDTH.FLEX}
						data-cy="custom-field-currency"
					>
						{(option: CurrencyOption) => (
							<ComboBox.Item key={option.value} textValue={option.label}>
								{option.label}
							</ComboBox.Item>
						)}
					</ComboBox>
				)}
				<FlexColumn gap={'l'}>
					{!!avaRefOptions && (
						<FlexColumn>
							<FlexRow>
								<Label>
									<FormattedMessage id={'settings.custom-fields.advanced_analytics_field_label'} />
								</Label>
								<IconTooltip>
									<IconTooltip.Item
										title={formatMessage({
											id: 'settings.custom-fields.create_modal.tooltip.advanced_analytics_field.title',
										})}
										details={formatMessage({
											id: 'settings.custom-fields.create_modal.tooltip.advanced_analytics_field.description',
										})}
									/>
								</IconTooltip>
							</FlexRow>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdownField>
										<Text ellipsis={true} size={'1'}>
											{selectedAvaRef.label}
										</Text>
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content offset={10} useTriggerWidth>
									{avaRefOptions.map(option => (
										<DropdownList.Item
											key={option.value}
											disabled={option.disabled}
											onClick={() => selectAvaRefOption(option)}
										>
											{option.label}
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						</FlexColumn>
					)}

					{isCreating && TYPE_INFO_MESSAGE[customFieldType] && (
						<Alert
							type={Alert.TYPE.INFORMATION}
							text={formatMessage({id: TYPE_INFO_MESSAGE[customFieldType]}, {maxLength: HYPERLINK_MAX_LENGTH})}
						/>
					)}

					{supportsReadOnly(customFieldType) && (
						<Checkbox
							checked={readOnly}
							onChange={() => setReadOnly(!readOnly)}
							labelText={formatMessage({id: 'settings.custom-fields.readOnly'})}
							notBoldLabel={true}
						/>
					)}
				</FlexColumn>
				{listVisibility && setListVisibility && isListVisibilityAvailable(entityType) && (
					<ListVisibilitySection
						value={listVisibility}
						onChange={setListVisibility}
						initialValue={initialListVisibility}
					/>
				)}
			</FlexColumn>
		</StyledContainer>
	);
};
